import { TransactionsHistory } from 'components/ui/transaction-history'

import { useCurrency } from 'utils/use-currency'

import {
  TransactionTypeView,
  TransactionStatusView,
} from 'components/ui/transaction-history/components/transaction/enum'

import {
  ETransactionStatuses,
  ETransactionTypes,
  ITransaction,
  TransactionAccountType,
} from '../../../../../../store/fetchers/users/types'

import { TransactionMain } from '../../../../../../components/ui/transaction-history/components/transaction'

import { TabTransactionsProps } from './tab-transactions.props'

export interface IAmountAndCoin {
  amount: string
  code: string
  network: string
  incoming: boolean
}

export interface ITransactionView {
  account_type: TransactionAccountType
  account_id: number
  type: TransactionTypeView
  id: string
  hash: string
  from_currency: string
  from_network: string
  from_amount: string
  to_currency: string
  to_network: string
  to_amount: string
  status: TransactionStatusView
  created_at: string
}

export const matchType = (type: ETransactionTypes, accountType: TransactionAccountType): TransactionTypeView => {
  if (type === ETransactionTypes.DEPOSIT && accountType === 'loan') {
    return TransactionTypeView.DEPOSIT
  }

  if (type === ETransactionTypes.DEPOSIT && accountType === 'wallet') {
    return TransactionTypeView.TOP_UP
  }

  if (type === ETransactionTypes.REPAYMENT) {
    return TransactionTypeView.REPAYMENT
  }

  if (type === ETransactionTypes.INTEREST) {
    return TransactionTypeView.INTEREST
  }

  if (type === ETransactionTypes.WITHDRAW && accountType === 'loan') {
    return TransactionTypeView.LOAN
  }

  if (type === ETransactionTypes.WITHDRAW && accountType === 'wallet') {
    return TransactionTypeView.WITHDRAWAL
  }

  if (type === ETransactionTypes.PLEDGE) {
    return TransactionTypeView.REDEMPTION
  }

  if (type === ETransactionTypes.EXCHANGE) {
    return TransactionTypeView.EXCHANGE
  }

  if (type === ETransactionTypes.COLLATERAL) {
    return TransactionTypeView.COLLATERAL
  }

  if (type === ETransactionTypes.LOAN) {
    return TransactionTypeView.LOAN
  }

  return null
}

export const matchStatus = (status: ETransactionStatuses): TransactionStatusView => {
  switch (status.toLowerCase()) {
    case ETransactionStatuses.PENDING:
      return TransactionStatusView.PENDING
    case ETransactionStatuses.CONFIRMING:
      return TransactionStatusView.CONFIRMING
    case ETransactionStatuses.PROCESSING:
    case ETransactionStatuses.REJECTED:
      return TransactionStatusView.PROCESSING
    case ETransactionStatuses.FINISHED:
      return TransactionStatusView.SUCCESS
    case ETransactionStatuses.REFUNDED:
      return TransactionStatusView.REFUNDED
    default:
      return null
  }
}

export const matchAmountAndCoin = (tx: ITransaction): IAmountAndCoin => {
  if (tx.transaction_type === ETransactionTypes.DEPOSIT) {
    return {
      amount: tx.to_amount ?? tx.from_amount,
      code: tx.from_currency,
      network: tx.from_network,
      incoming: true,
    }
  }

  if (
    tx.transaction_type === ETransactionTypes.REPAYMENT &&
    (tx.account_type === 'loan' || tx.account_type === 'saving')
  ) {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.REPAYMENT && tx.account_type === 'wallet') {
    return {
      amount: tx.from_amount,
      code: tx.from_currency,
      network: tx.from_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.INTEREST && tx.account_type === 'loan') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.INTEREST && tx.account_type === 'saving') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  if (tx.transaction_type === ETransactionTypes.WITHDRAW && tx.account_type === 'loan') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  if (tx.transaction_type === ETransactionTypes.INTEREST && tx.account_type === 'saving') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.PLEDGE) {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  return {
    amount: tx.from_amount,
    code: tx.from_currency,
    network: tx.from_network,
    incoming: true,
  }
}

export const TabTransactions = ({
  wallet,
  transactionList,
  search,
  setSearch,
  onSearchClick,
  isLoading
}: TabTransactionsProps) => {
  const fileNameCSV = `CoinRabbit_Wallet_${wallet.code.toUpperCase()}_${wallet.network.toUpperCase()}_history.csv`

  const currency = useCurrency({
    code: wallet.code,
    network: wallet.network,
  })

  const updatedList: TransactionMain[] = transactionList
    .map((tx) => {
      const type = matchType(tx.transaction_type, tx.account_type)
      const status = matchStatus(tx.status)

      const amountAndCoin = matchAmountAndCoin(tx)

      if (
        type === TransactionTypeView.EXCHANGE ||
        (tx.from_amount && tx.to_amount && tx.from_amount !== tx.to_amount && type !== TransactionTypeView.TOP_UP) ||
        tx.from_currency !== tx.to_currency ||
        tx.from_network !== tx.to_network
      ) {
        return {
          code: amountAndCoin.code,
          network: amountAndCoin.network,
          amount: amountAndCoin.amount,
          type: type,
          status: status,
          hash: tx.hash,
          label: type,
          date: tx.created_at,
          incoming: amountAndCoin.incoming,
          from_currency: {
            code: tx.from_currency,
            network: tx.from_network,
          },
          to_currency: {
            code: tx.to_currency,
            network: tx.to_network,
          },
          amountFrom: tx.from_amount,
          amountTo: tx.to_amount,
          id: tx.transaction_id.toString(),
        }
      } else
        return {
          amount: amountAndCoin.amount,
          code: amountAndCoin.code,
          network: amountAndCoin.network,
          type: type,
          status: status,
          hash: tx.hash,
          label: type,
          date: tx.created_at,
          incoming: amountAndCoin.incoming,
          id: tx.transaction_id.toString(),
        }
    })
    .filter((tx) => tx !== null && tx?.status !== null && tx?.type !== null)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  return (
    <TransactionsHistory
      search={search}
      setSearch={setSearch}
      onSearchClick={onSearchClick}
      list={updatedList}
      currency={currency}
      fileNameCSV={fileNameCSV}
      isLoading={isLoading}
    />
  )
}
